import { Preferences } from '@capacitor/preferences';
import { doc } from 'firebase/firestore';
import { updateDoc } from '../config/escrituras';
import { auth, db } from '../config/firebase';
import type { Tema } from '../context/ThemeContext';
import type { AjustesRecordatorios } from './recordatoriosGastos';
import type { AjustesAvisosTareas } from './avisosTareas';

/**
 * Preferencias guardadas en la cuenta (`usuarios/{uid}.preferencias`), para
 * que no se pierdan al reinstalar la app ni al entrar en otro dispositivo.
 *
 * Hay dos clases:
 * - Las de la cuenta (tema, vibraciones) se aplican solas en todos los
 *   dispositivos: el último cambio manda (SincronizarPreferencias).
 * - Las del dispositivo (Face ID, notificaciones, avisos, Recordatorios)
 *   necesitan un permiso de iOS que no se puede dar sin preguntar. La cuenta
 *   guarda lo que elegiste y, en un iPhone donde no están puestas (recién
 *   instalada o uno nuevo), se ofrece activarlas (PreferenciasDispositivo).
 *   Apagarlas en un iPhone no las apaga en los demás.
 *
 * Lo que ya se preguntó en este iPhone se guarda en él (Preferences), así que
 * al reinstalar se vuelve a preguntar, que es justo lo que se busca.
 */

export interface PreferenciasCuenta {
  tema?: Tema;
  vibraciones?: boolean;
  bloqueo?: boolean;
  notificaciones?: boolean;
  recordatoriosGastos?: AjustesRecordatorios;
  avisosTareas?: AjustesAvisosTareas;
  recordatoriosTareas?: boolean;
}

export type AjusteDispositivo = 'bloqueo' | 'notificaciones' | 'recordatoriosGastos' | 'avisosTareas' | 'recordatoriosTareas';

export const AJUSTES_DISPOSITIVO: AjusteDispositivo[] =
  ['bloqueo', 'notificaciones', 'recordatoriosGastos', 'avisosTareas', 'recordatoriosTareas'];

/** Si en la cuenta consta encendido (undefined: nunca se guardó). */
export function encendidoEnCuenta(p: PreferenciasCuenta, clave: AjusteDispositivo): boolean | undefined {
  switch (clave) {
    case 'recordatoriosGastos': {
      const r = p.recordatoriosGastos;
      return r && (r.inactividad || r.diario || r.resumenMes);
    }
    case 'avisosTareas': return p.avisosTareas?.activos;
    default: return p[clave];
  }
}

/**
 * Qué ofrecer activar en este dispositivo: lo encendido en la cuenta que aquí
 * no lo está, se puede usar y no se ha preguntado ya. Pura, para probarla.
 * `aqui[clave]` es null si en este dispositivo no se puede usar.
 */
export function queOfrecer(
  cuenta: PreferenciasCuenta,
  aqui: Record<AjusteDispositivo, boolean | null>,
  respondidas: ReadonlySet<string>,
): AjusteDispositivo[] {
  return AJUSTES_DISPOSITIVO.filter(c =>
    encendidoEnCuenta(cuenta, c) === true && aqui[c] === false && !respondidas.has(c));
}

/**
 * Lo encendido aquí que la cuenta aún no conoce: así, quien ya lo tenía antes
 * de esta versión no lo pierde al reinstalar. Pura, para probarla.
 */
export function queSembrar(
  cuenta: PreferenciasCuenta, aqui: Record<AjusteDispositivo, boolean | null>,
): AjusteDispositivo[] {
  return AJUSTES_DISPOSITIVO.filter(c => aqui[c] === true && encendidoEnCuenta(cuenta, c) === undefined);
}

/** Guarda en la cuenta. Sin sesión no hace nada; si falla, se queda en el dispositivo. */
export function guardarEnCuenta(cambios: Partial<PreferenciasCuenta>): void {
  const uid = auth.currentUser?.uid;
  const campos = Object.entries(cambios).filter(([, v]) => v !== undefined);
  if (!uid || !campos.length) return;
  updateDoc(doc(db, 'usuarios', uid), Object.fromEntries(campos.map(([k, v]) => [`preferencias.${k}`, v])))
    .catch(e => console.warn('No se pudieron guardar las preferencias', e));
}

const claveRespondidas = (uid: string) => `preferenciasRespondidas.${uid}`;

export async function leerRespondidas(uid: string): Promise<Set<string>> {
  const { value } = await Preferences.get({ key: claveRespondidas(uid) });
  try { return new Set(value ? JSON.parse(value) : []); } catch { return new Set(); }
}

export async function marcarRespondidas(uid: string, claves: AjusteDispositivo[]): Promise<void> {
  const ya = await leerRespondidas(uid);
  claves.forEach(c => ya.add(c));
  await Preferences.set({ key: claveRespondidas(uid), value: JSON.stringify(Array.from(ya)) });
}

/**
 * Al cambiar a mano un ajuste del dispositivo: se guarda en la cuenta (para
 * ofrecerlo en los demás) y aquí ya no se vuelve a preguntar por él.
 */
export function recordarAjusteDispositivo(cambios: Partial<PreferenciasCuenta>): void {
  guardarEnCuenta(cambios);
  const uid = auth.currentUser?.uid;
  const claves = AJUSTES_DISPOSITIVO.filter(c => c in cambios);
  if (uid && claves.length) marcarRespondidas(uid, claves).catch(() => {});
}
